import {
  PaperClipIcon,
  CheckIcon,
  EnvelopeIcon,
  ChevronDownIcon as ChevronDown20,
  ArrowUpCircleIcon,
  StopIcon,
  StopCircleIcon,
  EyeIcon,
} from "@heroicons/react/20/solid";
import {
  Candidate,
  Role,
  CareerTimelineType,
  RoleStatus,
  CandidateStatus,
  CandidateRoleAnswerModel,
} from "../../Models";
import { Link, useParams, useLocation } from "react-router-dom";
import GitHubIcon from "@mui/icons-material/GitHub";
import Review from "./Review";
import RecruitmentTimelineModal from "../Shared/RecruitmentTimelineModal/RecruitmentTimelineModal";
import TimelineModal from "../Shared/TimelineModal";
import StraightTimeline from "./StraightTimerline";
import CareerTimeline from "./CareerTimeline";
import GradeMetricsModal from "./GradeMetricsModal";
import { ChevronDownIcon } from "@heroicons/react/24/solid";
import { useEffect, useState, Fragment } from "react";
import {
  GlobeAltIcon,
  MapPinIcon,
  EnvelopeIcon as EnvelopeOutline,
  CalendarIcon,
  LinkIcon,
  BriefcaseIcon,
  AcademicCapIcon,
  PaperClipIcon as PaperClipOutline,
} from "@heroicons/react/24/outline";
import { XMarkIcon } from "@heroicons/react/24/outline";
import {
  fetchComments,
  GetCandidateData,
  submitReview,
} from "../../api/assignments.api";
import useApiToken from "../../hooks/useApiToken";
import { GetCandidateRoleAnswers } from "../../api/assessmentquestion.api";
import {
  AppliedRolesForCandidate,
  GetCandidateCv,
  GetCandidateTimeline,
  CandidateCvFlagResult,
} from "../../api/candidates.api";
import  parse from "html-react-parser";
import Avatar from "../Shared/Avatar";
import { Listbox, Transition } from "@headlessui/react";
import { Spinner } from "../Shared/Spinner";
import InterviewAssessmentDropdown from "../Shared/Dropdowns/InterviewAssessmentDropdown";
import { ArrowDownward, ArrowForward } from "@mui/icons-material";
import CVFlagsCard from "./CVFlagsCard";
import { GetCandidateProjectChoicesForRecruiter } from "../../api/jobdescription.api";


type FileType = {
  type: string;
  mime: string;
};

interface PDFModalState {
  isOpen: boolean;
  pdfUrl: string;
  fileName: string;
}

const PDFViewerModal: React.FC<{
  isOpen: boolean;
  onClose: () => void;
  pdfUrl: string;
  fileName: string;
}> = ({ isOpen, onClose, pdfUrl, fileName }) => {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex min-h-screen items-center justify-center p-4">
        <div
          className="fixed inset-0 bg-black bg-opacity-50 transition-opacity"
          onClick={onClose}
        />
        <div className="relative w-full max-w-6xl max-h-[90vh] bg-white dark:bg-darkbglight rounded-lg shadow-xl">
          <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-800">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-300">
              {fileName}
            </h3>
            <button
              onClick={onClose}
              className="text-gray-600 dark:text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
            >
              <XMarkIcon className="w-6 h-6" />
            </button>
          </div>
          <div className="p-4 h-[calc(90vh-120px)]">
            <iframe
              src={pdfUrl}
              className="w-full h-full border-0 rounded"
              title={fileName}
            />
          </div>
          <div className="flex items-center justify-between p-4 border-t border-gray-200 dark:border-gray-800">
            <div className="text-sm text-gray-500 dark:text-gray-400">
              PDF Viewer
            </div>
            <div className="flex gap-2">
              <a
                href={pdfUrl}
                download={fileName}
                className="px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-md transition-colors"
              >
                Download
              </a>
              <button
                onClick={onClose}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 dark:bg-gray-700 dark:text-gray-300 dark:hover:bg-gray-600 rounded-md transition-colors"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

type advanceWithEmailOption = { name: string; description: string };
type disqualifyWithEmailOption = { name: string; description: string };

const advanceWithEmailOptions: advanceWithEmailOption[] = [
  {
    name: "Advance with Email Notification",
    description:
      "Candidate will be advanced to the next steps, and an email notification will be sent to them regarding the next steps.",
  },
  {
    name: "Advance without Email Notification",
    description:
      "Candidate will be advanced to the next round without sending any email notification.",
  },
];

const disqualifyWithEmailOptions: disqualifyWithEmailOption[] = [
  {
    name: "Disqualify with Email Notification",
    description:
      "Candidate will be disqualified and an email notification will be sent to them regarding the decision.",
  },
  {
    name: "Disqualify without Email Notification",
    description:
      "Candidate will be disqualified without sending any email notification.",
  },
];

function classNames(...classes: string[]) {
  return classes.filter(Boolean).join(" ");
}

const getFileType = (base64String: string | null | undefined): FileType | null => {
  if (!base64String) return null;
  const fileType: FileType = { type: "", mime: "" };
  if (base64String.startsWith("JVB")) {
    fileType.mime = "data:application/pdf;base64,";
    fileType.type = "pdf";
  } else if (base64String.startsWith("UEs")) {
    fileType.mime =
      "data:application/vnd.openxmlformats-officedocument.wordprocessingml.document;base64,";
    fileType.type = "docx";
  } else {
    return null;
  }
  return fileType;
};

// ─── Small reusable section card ───────────────────────────────────────────────
function SectionCard({
  title,
  children,
}: {
  title: string;
  children: React.ReactNode;
}) {
  return (
    <div className=" p-2 bg-white dark:bg-darkbglight rounded-xl shadow-sm border border-gray-100 dark:border-gray-700">
      <div className="px-6 py-4 border-b border-gray-100 dark:border-gray-700">
        <h3 className="text-base font-semibold text-gray-900 dark:text-gray-100">
          {title}
        </h3>
      </div>
      <div className="px-6 py-5">{children}</div>
    </div>
  );
}

// ─── Info row used in Personal Information ─────────────────────────────────────
function InfoRow({
  icon,
  label,
  children,
}: {
  icon: React.ReactNode;
  label: string;
  children: React.ReactNode;
}) {
  return (
    <div className="flex items-start gap-3">
      <div className="mt-0.5 flex-shrink-0 text-gray-500 dark:text-gray-500">
        {icon}
      </div>
      <div>
        <p className="text-xs text-gray-500 dark:text-gray-500">{label}</p>
        <div className="text-sm font-medium text-gray-800 dark:text-gray-100 mt-0.5">
          {children}
        </div>
      </div>
    </div>
  );
}

export default function CandidateDetails({
  candidate,
  candidateRole,
  disqualifiedOrHired,
  advanceStatusSpinner,
  setDisqualifyConfirmModal,
  selectedAssessment,
  setSelectedAssessment,
  interviewAssessments,
  advanceWithEmail,
  setAdvanceWithEmail,
  disqualifyWithEmail,
  setDisqualifyWithEmail,
  setAdvanceStatusConfirmModal,
  includeSlot,
  setIncludeSlot,
  availableSlots,
  timeline,
  nextTimelineIndex,
  handleDisqualify,
  handleAdvanceToNextStatus,
  roleStatus,
  cvFlagResult,
  isFetchingCvFlags,
  cvFlagError,
  handleGetCvFlags,
}: {
  candidate: Candidate | undefined;
  candidateRole: Role[];
  disqualifiedOrHired: RoleStatus | null;
  advanceStatusSpinner: boolean;
  setDisqualifyConfirmModal: (value: boolean) => void;
  selectedAssessment: any;
  setSelectedAssessment: (value: any) => void;
  interviewAssessments: any[];
  advanceWithEmail: advanceWithEmailOption;
  setAdvanceWithEmail: (value: advanceWithEmailOption) => void;
  disqualifyWithEmail: disqualifyWithEmailOption;
  setDisqualifyWithEmail: (value: disqualifyWithEmailOption) => void;
  setAdvanceStatusConfirmModal: (value: boolean) => void;
  includeSlot: boolean;
  setIncludeSlot: (value: boolean) => void;
  availableSlots: any[];
  timeline: any[];
  nextTimelineIndex: number;
  handleDisqualify: () => void;
  handleAdvanceToNextStatus: () => void;
  roleStatus: RoleStatus[];
  cvFlagResult?: CandidateCvFlagResult | null;
  isFetchingCvFlags?: boolean;
  cvFlagError?: string | null;
  handleGetCvFlags?: () => void;
}) {
  const [hoveredCandidateId, setHoveredCandidateId] = useState<string | null>(
    null,
  );
  const [timelineLoading, setTimelineLoading] = useState(false);
  const [timelineError, setTimelineError] = useState(false);
  const [timelineData, setTimelineData] = useState<CareerTimelineType | null>(
    null,
  );
  const [appliedRolesList, setAppliedRolesList] = useState<any[]>([]);
  const token = useApiToken();
  const [showReviewbox, setShowReviewbox] = useState<boolean>(false);
  const [githublink, setgithublink] = useState("");
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [liveProjectLink, setLiveProjectLink] = useState("");
  const [previousReview, setpreviousReview] = useState("");
  const { roleId, candidateId } = useParams();
  const location = useLocation();
  const [pdfModal, setPdfModal] = useState<PDFModalState>({
    isOpen: false,
    pdfUrl: "",
    fileName: "",
  });
  const [gradeModalState, setGradeModalState] = useState<{
    isOpen: boolean;
    type: "combined" | "gap" | null;
  }>({ isOpen: false, type: null });
  const [cvBlobUrl, setCvBlobUrl] = useState<string | null>(null);
  const [cvExtension, setCvExtension] = useState<string>("pdf");
  const [candidateRoleAnswers, setCandidateRoleAnswers] = useState<
    CandidateRoleAnswerModel[]
  >([]);
  useEffect(() => {
    const fetchRoleAnswers = async () => {
      if (!candidateId || !roleId || !token) {
        setCandidateRoleAnswers([]);
        return;
      }

      // Reset previous role answers immediately to avoid cross-role flash.
      setCandidateRoleAnswers([]);
      try {
        const answers = await GetCandidateRoleAnswers(candidateId, roleId, token);
        setCandidateRoleAnswers(answers || []);
      } catch (error) {
        console.error("Error fetching candidate role answers:", error);
        setCandidateRoleAnswers([]);
      }
    };
    fetchRoleAnswers();
  }, [candidateId, roleId, token]);
  const [chosenProjectTitles, setChosenProjectTitles] = useState<string[]>([]);
  const [chosenProjectsLoading, setChosenProjectsLoading] = useState(false);
  useEffect(() => {
    if (!candidateId || !roleId || !token) {
      setChosenProjectTitles([]);
      return;
    }
    setChosenProjectsLoading(true);
    GetCandidateProjectChoicesForRecruiter(candidateId, roleId, token)
      .then((titles) => setChosenProjectTitles(titles))
      .catch(() => setChosenProjectTitles([]))
      .finally(() => setChosenProjectsLoading(false));
  }, [candidateId, roleId, token]);
  useEffect(() => {
    if (!candidateId || !token) return;
    let objectUrl: string | null = null;
    GetCandidateCv(candidateId, token)
      .then((blob) => {
        objectUrl = URL.createObjectURL(blob);
        setCvBlobUrl(objectUrl);
        setCvExtension(blob.type.includes("pdf") ? "pdf" : "docx");
      })
      .catch(() => setCvBlobUrl(null));
    return () => {
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [candidateId, token]);

  const openGradeModal = (type: "combined" | "gap") =>
    setGradeModalState({ isOpen: true, type });
  const closeGradeModal = () =>
    setGradeModalState({ isOpen: false, type: null });

  const handleViewPDF = (attachment: { name: string; href: string }): void => {
    setPdfModal({
      isOpen: true,
      pdfUrl: attachment.href,
      fileName: attachment.name,
    });
  };
  const closePDFModal = (): void =>
    setPdfModal({ isOpen: false, pdfUrl: "", fileName: "" });

  const handleTimelineClick = async (
    e: React.MouseEvent,
    candidate: Candidate,
  ) => {
    e.preventDefault();
    setTimelineLoading(true);
    try {
      if (roleId !== undefined && token !== undefined) {
        const candidateTimeline = await GetCandidateTimeline(
          roleId,
          candidate.id,
          token,
        );
        setTimelineData(candidateTimeline);
        setTimelineError(false);
        setTimelineLoading(false);
      }
    } catch (error) {
      console.error("Error fetching candidate timeline:", error);
      setTimelineError(true);
    }
    setIsModalOpen(true);
  };

  const checkAssignmentOnPageLoad = async () => {
    if (!candidate?.email || !roleId || !token) return;
    try {
      const userdata = await GetCandidateData(candidate?.email, roleId, token);
      if (userdata.assignmentLink != null) {
        setgithublink(userdata.assignmentLink);
        setpreviousReview(userdata.comments);
        setLiveProjectLink(userdata.liveProjectLink);
      }
    } catch (error) {
      console.error("Error checking assignment submission:", error);
    }
  };

  useEffect(() => {
    checkAssignmentOnPageLoad();
  }, [roleId, candidate, token]);

  async function getcomments(candidateId: any, roleId: any) {
    if (!token) return;
    try {
      const review = await fetchComments(candidateId, roleId, token);
      setpreviousReview(review.comment);
    } catch (error) {
      console.error("Error getting review:");
    }
  }

  const handleCommentSubmit = async (comment: string) => {
    if (!token) return;
    try {
      await submitReview(
        { candidateId, roleId, assignmentLink: githublink, comments: comment },
        token,
      );
      setShowReviewbox(false);
    } catch (error) {
      console.error("Error handling assignment submission:", error);
    }
    getcomments(candidateId, roleId);
  };

  useEffect(() => {
    const fetchAppliedRoles = async () => {
      if (candidateId && token) {
        try {
          const roles = await AppliedRolesForCandidate(candidateId, token);
          setAppliedRolesList(roles || []);
        } catch (error) {
          console.error("Error fetching applied roles:", error);
        }
      }
    };
    fetchAppliedRoles();
  }, [candidateId, token]);

  let cvName, coverName;
  const attachments: { name: string; href: string }[] = [];
  if (candidate) {
    const coverFileType = getFileType(candidate.coverLetter);
    if (coverFileType) {
      coverName = `cover_${candidate.firstName}_${candidate.lastName}.${coverFileType.type}`;
      attachments.push({
        name: coverName,
        href: coverFileType.mime + candidate.coverLetter,
      });
    }
    if (cvBlobUrl) {
      cvName = `resume_${candidate.firstName}_${candidate.lastName}.${cvExtension}`;
      attachments.push({ name: cvName, href: cvBlobUrl });
    }
  }

  useEffect(() => {
    const fetchAppliedRoles = async () => {
      if (candidateId && token) {
        try {
          const roles = await AppliedRolesForCandidate(candidateId, token);
          setAppliedRolesList(roles || []);
        } catch (error) {
          console.error("Error fetching applied roles:", error);
        }
      }
    };
    fetchAppliedRoles();
  }, [candidateId, token]);

  const handleToggleSlot = () => {
    setIncludeSlot(!includeSlot);
  };

  const groupedCandidateRoleAnswers = Array.from(
    candidateRoleAnswers
      .reduce((map, item) => {
        const key = item.questionId || item.questionText;
        if (!map.has(key)) {
          map.set(key, {
            questionText: item.questionText,
            answers: [] as string[],
          });
        }

        const group = map.get(key);
        if (!group) return map;

        const normalizedAnswer = (item.answerText || "").trim();
        if (normalizedAnswer && !group.answers.includes(normalizedAnswer)) {
          group.answers.push(normalizedAnswer);
        }

        return map;
      }, new Map<string, { questionText: string; answers: string[] }>())
      .values(),
  );

  return (
    <>
      <section
        aria-labelledby="applicant-information-title"
        className="space-y-4 overflow-visible"
      >
        <div className="bg-white shadow-sm sm:rounded-xl dark:bg-darkbglight border border-gray-100 dark:border-gray-700">
          <div className="px-6 py-8 sm:px-6 border-gray-200 dark:border-gray-700 dark:bg-gray-800">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-6">
              <div className="flex items-center gap-6">
                <div className="flex-shrink-0">
                  <div className="flex items-center justify-center h-16 w-16 rounded-full bg-entntblue text-white text-xl font-bold shadow-lg">
                    {candidate?.firstName?.charAt(0)}
                    {candidate?.lastName?.charAt(0)}
                  </div>
                </div>
                <div>
                  <div className="-mt-4">
                    <h2
                      id="applicant-information-title"
                      className="text-xl font-semibold leading-8 text-gray-900 dark:text-gray-100"
                    >
                      {candidate?.firstName} {candidate?.lastName}
                    </h2>
                    <div className="flex items-center gap-1">
                      <span className={`text-sm ${appliedRolesList.filter((r) => r.roleId === roleId)[0]?.roleStatusName?.toLowerCase().includes('disqualified') ? "text-red-400 dark:text-red-400" : "text-green-500 dark:text-green-500"} font-semibold`}>{appliedRolesList.filter((r) => r.roleId === roleId)[0]?.roleStatusName || 'applied'}</span>
                      {!appliedRolesList.filter((r) => r.roleId === roleId)[0]?.roleStatusName?.toLowerCase().includes('disqualified') && <span className="h-1.5 max-h-1.5 w-1.5 max-w-1.5 rounded-full bg-green-500 inline-block " />}
                    </div>
                  </div>
                </div>
              </div>
              <div className="flex flex-col-reverse sm:flex-row sm:justify-end w-full sm:w-auto">
                {disqualifiedOrHired === null ? (
                  advanceStatusSpinner ? (
                    <span className="inline-flex items-center justify-center rounded-md bg-gray-100 px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 dark:bg-gray-700 dark:text-gray-200">
                      <Spinner />
                      <span className="ml-2">Advancing</span>
                    </span>
                  ) : (
                    <>
                      <Listbox
                        as="div"
                        value={disqualifyWithEmail}
                        onChange={(option) => {
                          setDisqualifyWithEmail(option);
                          setDisqualifyConfirmModal(true);
                        }}
                      >
                        {({ open }) => (
                          <div className="relative">
                            <Listbox.Button className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-medium text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 border border-transparent hover:border-red-200 transition-all">
                              <StopCircleIcon className="h-4 w-4" />
                              Disqualify
                            </Listbox.Button>
                            <Transition
                              show={open}
                              as={Fragment}
                              leave="transition ease-in duration-100"
                              leaveFrom="opacity-100"
                              leaveTo="opacity-0"
                            >
                              <Listbox.Options className="absolute right-0 z-50 mt-2 w-72 origin-top-right divide-y divide-gray-200 overflow-hidden rounded-md bg-white shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none sm:left-auto dark:divide-gray-700 dark:bg-gray-800">
                                {disqualifyWithEmailOptions.map((option) => (
                                  <Listbox.Option
                                    key={option.name}
                                    className={({ active }) =>
                                      classNames(
                                        active
                                          ? "bg-red-600 text-white"
                                          : "text-gray-900 dark:text-gray-300",
                                        "cursor-default select-none p-4 text-sm",
                                      )
                                    }
                                    value={option}
                                  >
                                    {({ selected, active }) => (
                                      <div className="flex flex-col">
                                        <div className="flex justify-between items-center">
                                          <p
                                            className={
                                              selected
                                                ? "font-semibold"
                                                : "font-normal"
                                            }
                                          >
                                            {option.name}
                                          </p>
                                          {selected ? (
                                            <CheckIcon
                                              className="h-5 w-5 ml-2"
                                              aria-hidden="true"
                                            />
                                          ) : null}
                                        </div>
                                        <p
                                          className={classNames(
                                            active
                                              ? "text-white"
                                              : "text-gray-500 dark:text-gray-600",
                                            "mt-2 text-xs",
                                          )}
                                        >
                                          {option.description}
                                        </p>
                                      </div>
                                    )}
                                  </Listbox.Option>
                                ))}
                              </Listbox.Options>
                            </Transition>
                          </div>
                        )}
                      </Listbox>
                      <Listbox
                        as="div"
                        value={advanceWithEmail}
                        onChange={(option) => {
                          setAdvanceWithEmail(option);
                          setAdvanceStatusConfirmModal(true);
                        }}
                      >
                        {({ open }) => (
                          <div className="relative">
                            <Listbox.Button className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-medium text-green-600 hover:bg-green-50 dark:hover:bg-green-900/20 border border-transparent hover:border-green-200 transition-all">
                              <ArrowUpCircleIcon className="h-4 w-4" />
                              Advance
                            </Listbox.Button>
                            <Transition
                              show={open}
                              as={Fragment}
                              leave="transition ease-in duration-100"
                              leaveFrom="opacity-100"
                              leaveTo="opacity-0"
                            >
                              <Listbox.Options className="absolute right-0 z-50 mt-2 w-72 origin-top-right divide-y divide-gray-200 overflow-hidden rounded-md bg-white shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none sm:left-auto dark:divide-gray-700 dark:bg-gray-800">
                                {advanceWithEmailOptions.map((option) => (
                                  <Listbox.Option
                                    key={option.name}
                                    className={({ active }) =>
                                      classNames(
                                        active
                                          ? "bg-entntblue text-white"
                                          : "text-gray-900 dark:text-gray-300",
                                        "cursor-default select-none p-4 text-sm",
                                      )
                                    }
                                    value={option}
                                  >
                                    {({ selected, active }) => (
                                      <div className="flex flex-col">
                                        <div className="flex justify-between items-center">
                                          <p
                                            className={
                                              selected
                                                ? "font-semibold"
                                                : "font-normal"
                                            }
                                          >
                                            {option.name}
                                          </p>
                                          {selected ? (
                                            <CheckIcon
                                              className="h-5 w-5 ml-2"
                                              aria-hidden="true"
                                            />
                                          ) : null}
                                        </div>
                                        <p
                                          className={classNames(
                                            active
                                              ? "text-white"
                                              : "text-gray-500 dark:text-gray-600",
                                            "mt-2 text-xs",
                                          )}
                                        >
                                          {option.description}
                                        </p>
                                      </div>
                                    )}
                                  </Listbox.Option>
                                ))}
                              </Listbox.Options>
                            </Transition>
                          </div>
                        )}
                      </Listbox>
                    </>
                  )
                ) : (
                  <span className="inline-flex items-center justify-center rounded-md bg-green-100 px-3 py-2 text-sm font-semibold text-green-800 dark:bg-green-900 dark:text-green-200">
                    {disqualifiedOrHired?.name}
                  </span>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* ── Personal Information ─────────────────────────────────── */}
        <SectionCard title="Personal information">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-8">
            <InfoRow
              icon={<EnvelopeOutline className="h-4 w-4" />}
              label="mail"
            >
              <span className="break-all">{candidate?.email ?? "—"}</span>
            </InfoRow>

            <InfoRow
              icon={<CalendarIcon className="h-4 w-4" />}
              label={
                candidate?.dateOfBirth
                  ? `${Math.floor((Date.now() - new Date(candidate.dateOfBirth).getTime()) / (1000 * 60 * 60 * 24 * 365))} years old`
                  : ""
              }
            >
              {candidate?.dateOfBirth
                ? new Date(candidate.dateOfBirth).toLocaleDateString(
                    undefined,
                    { year: "numeric", month: "long", day: "numeric" },
                  )
                : "—"}
            </InfoRow>

            <InfoRow
              icon={<MapPinIcon className="h-4 w-4" />}
              label="work locations"
            >
              {candidate?.workLocation1City?.name ? (
                <>
                  {candidate.workLocation1City.name}
                  {candidate.workLocation2City && (
                    <span>
                      {" "}
                      &nbsp;|&nbsp; {candidate.workLocation2City.name}
                    </span>
                  )}
                </>
              ) : (
                "—"
              )}
            </InfoRow>

            <InfoRow icon={<LinkIcon className="h-4 w-4" />} label="linkedin">
              {candidate?.linkedinProfile ? (
                <a
                  href={candidate.linkedinProfile.startsWith('http') ? candidate.linkedinProfile : `https://${candidate.linkedinProfile}`}
                  target="_blank"
                  // rel="noopener noreferrer"
                  className="text-entntblue hover:underline"
                >
                  {candidate.firstName} {candidate.lastName}
                </a>
              ) : (
                <span className="text-gray-600 dark:text-gray-400 font-normal">Not provided</span>
              )}
            </InfoRow>

            {/* Career Timeline */}
            <InfoRow
              icon={<CalendarIcon className="h-4 w-4" />}
              label="career timeline"
            >
              {(candidate?.experience && candidate.experience.length > 0) || (candidate?.education && candidate.education.length > 0) ? (
                <span
                  className="text-entntblue hover:underline cursor-pointer relative"
                  onMouseEnter={(e) => {
                    if (candidate?.id) {
                      setHoveredCandidateId(candidate.id);
                      handleTimelineClick(e as any, candidate);
                    }
                  }}
                  onMouseLeave={() => setHoveredCandidateId(null)}
                  // onClick={(e) => {
                  //   if (candidate?.id) {
                  //     handleTimelineClick(e as any, candidate);
                  //     setHoveredCandidateId(candidate.id);
                  //   }
                  // }}
                >
                  View Timeline
                {timelineData?.candidateId === candidate?.id &&
                  hoveredCandidateId === candidate?.id &&
                  timelineData &&
                  candidate && (
                      <TimelineModal
                        open={true}
                        setOpen={(open) => !open && setHoveredCandidateId(null)}
                        content={timelineData}
                        loading={timelineLoading}
                        timelineError={timelineError}
                        timeline={
                          timelineData.timeline.length <= 3 ? (
                            <StraightTimeline timeline={timelineData.timeline} />
                          ) : (
                            <CareerTimeline
                              timeline={timelineData.timeline || []}
                            />
                          )
                        }
                      />
                    )}
                </span>
              ) : (
                <span className="text-gray-600 dark:text-gray-400 font-normal">No Education and Experience</span>
              )}
            </InfoRow>
          </div>
        </SectionCard>
        {/* ── Other Roles Applied ──────────────────────────────────── */}
        <SectionCard title="Applications">
          {/* Current Role */}
          <div className="mb-5">
            <p className="text-xs font-semibold text-gray-600 dark:text-gray-500 uppercase tracking-wider mb-3">
              Current role
            </p>
            {candidateRole[0] ? (
              <div className="flex items-center gap-3 p-3 rounded-xl bg-gray-50 dark:bg-gray-800  border-gray-200 dark:border-gray-700">
                <div className="h-8 w-8 rounded-lg bg-entntblue/10 dark:bg-entntblue/20 flex items-center justify-center flex-shrink-0">
                  <BriefcaseIcon className="h-4 w-4 text-entntblue" />
                </div>
                <div className="min-w-0">
                  <p className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate">
                    {candidateRole[0]?.title ?? "—"}
                  </p>
                  <div className="flex items-center gap-1.5 mt-0.5">
                    <span className="text-xs font-medium text-entntorange">
                      {appliedRolesList.filter((r) => r.roleId === roleId)[0]?.roleStatusName || 'applied'}
                    </span>
                    <span className="h-1.5 w-1.5 rounded-full bg-green-500 inline-block" />
                  </div>
                </div>
              </div>
            ) : (
              <p className="text-sm text-gray-600 dark:text-gray-400">No current role</p>
            )}
          </div>

          {/* Divider */}
          <div className="border-t border-gray-100 dark:border-gray-700 mb-5" />

          {/* Other Roles */}
          <div>
            <p className="text-xs font-semibold text-gray-600 dark:text-gray-500 uppercase tracking-wider mb-3">
              Other roles applied
            </p>
            {appliedRolesList.filter((r) => r.roleId !== candidateRole[0]?.id)
              .length === 0 ? (
              <p className="text-sm text-gray-600 dark:text-gray-400">No other roles applied.</p>
            ) : (
              <ul className="space-y-2">
                {appliedRolesList
                  .filter((role) => role.roleId !== candidateRole[0]?.id)
                  .map((role, key) => (
                    <li key={role.roleId}>
                      <Link
                        to={`/roles/${role.roleId}/candidates/${candidate?.id}`}
                        className="flex items-center justify-between gap-2 p-3 rounded-xl bg-gray-50 dark:bg-gray-800 border-gray-200 dark:border-gray-700 hover:border-entntblue dark:hover:border-entntblue hover:bg-blue-50 dark:hover:bg-entntblue/10 transition-all group"
                      >
                        <div className="flex items-center gap-3 min-w-0">
                          <div className="h-8 w-8 rounded-lg bg-gray-200 dark:bg-gray-700 flex items-center justify-center flex-shrink-0">
                            <BriefcaseIcon className="h-4 w-4 text-gray-600 dark:text-gray-400 group-hover:text-entntblue transition-colors" />
                          </div>
                          <div className="min-w-0">
                            <p className="text-sm font-medium text-gray-800 dark:text-gray-100 truncate group-hover:text-entntblue transition-colors">
                              {role.roleTitle
                                ? role.roleTitle
                                : `Role ${key + 1}`}
                            </p>
                            <div className="flex items-center gap-1.5 mt-0.5">
                              <span className="text-xs text-gray-600 dark:text-gray-400">
                                {role?.roleStatusName || 'applied'}
                              </span>
                              <span className="h-1.5 w-1.5 rounded-full bg-green-500 inline-block" />
                            </div>
                          </div>
                        </div>
                        <span className="text-gray-300 group-hover:text-entntblue transition-colors text-sm flex-shrink-0">
                          <ArrowForward />
                        </span>
                      </Link>
                    </li>
                  ))}
              </ul>
            )}
          </div>
        </SectionCard>

        {/* ── Chosen Project(s) ────────────────────────────────────── */}
        <SectionCard title="Chosen Project(s)">
          {chosenProjectsLoading ? (
            <Spinner />
          ) : chosenProjectTitles.length === 0 ? (
            <p className="text-sm text-gray-600 dark:text-gray-400">
              Candidate has not chosen any projects yet.
            </p>
          ) : (
            <ul className="flex flex-wrap gap-2">
              {chosenProjectTitles.map((title) => (
                <li
                  key={title}
                  className="inline-flex items-center rounded-md bg-gray-50 dark:bg-gray-800 px-2.5 py-1 text-xs font-medium text-gray-700 dark:text-gray-200 border border-gray-200 dark:border-gray-700"
                >
                  {title}
                </li>
              ))}
            </ul>
          )}
        </SectionCard>

        {/* ── Resume / Attachments ─────────────────────────────────── */}
        {attachments.length > 0 && (
          <SectionCard title="Resume">
            <div className="space-y-2">
              {attachments.map((attachment) =>
                attachment ? (
                  <div
                    key={attachment.name}
                    className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700"
                  >
                    <div className="flex items-center gap-3 min-w-0">
                      <PaperClipIcon
                        className="h-5 w-5 flex-shrink-0 text-gray-600 dark:text-gray-400"
                        aria-hidden="true"
                      />
                      <span className="text-sm font-medium text-gray-800 dark:text-gray-100 truncate">
                        {attachment.name}
                      </span>
                    </div>
                    <div className="flex items-center gap-2 ml-4 flex-shrink-0">
                      <button
                        onClick={() => handleViewPDF(attachment)}
                        className="inline-flex items-center gap-1.5 px-2 py-1 rounded-lg text-sm font-medium text-gray-500 hover:text-entntorange border border-transparent transition-all"
                      >
                        <EyeIcon className="h-4 w-4 inline" />
                        View
                      </button>
                      <a
                        href={attachment.href}
                        download={attachment.name}
                        className="inline-flex items-center gap-1.5 px-2 py-1 rounded-lg text-sm font-medium text-gray-500 hover:text-entntblue border border-transparent transition-all"
                      >
                        <ArrowDownward className="h-4 w-4 max-h-4 max-w-4 inline" />
                        Download
                      </a>
                    </div>
                  </div>
                ) : null,
              )}
            </div>
          </SectionCard>
        )}

        {handleGetCvFlags && (
          <CVFlagsCard
            cvFlagResult={cvFlagResult ?? null}
            isFetchingCvFlags={isFetchingCvFlags ?? false}
            cvFlagError={cvFlagError ?? null}
            handleGetCvFlags={handleGetCvFlags}
          />
        )}
        {/* ── Experience ───────────────────────────────────────────── */}
        <SectionCard title="Experience">
          {!candidate?.experience || candidate.experience.length === 0 ? (
            <p className="text-sm text-gray-600 dark:text-gray-400">
              Candidate does not have any experience
            </p>
          ) : (
            <div className="divide-y divide-gray-100 dark:divide-gray-700 -mx-6">
              {candidate.experience.map((exp, idx) => (
                <div
                  key={exp.company?.id + exp.title + idx}
                  className="flex items-start gap-4 px-6 py-4 hover:bg-gray-50 dark:hover:bg-gray-800/40 transition-colors"
                >
                  <div className="h-9 w-9 rounded-lg bg-gray-100 dark:bg-gray-700 flex items-center justify-center flex-shrink-0">
                    <BriefcaseIcon className="h-4 w-4 text-gray-600 dark:text-gray-400" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold text-gray-900 dark:text-gray-100">
                      {exp.company?.name ?? "—"}
                    </p>
                    <p className="text-sm text-gray-500 dark:text-gray-400">
                      {exp.title}
                    </p>
                    <div className="flex flex-wrap items-center gap-x-3 gap-y-0.5 mt-1">
                      <span className="text-xs text-gray-600 dark:text-gray-400">
                        {exp.period}
                      </span>
                      {exp.company?.city?.name && (
                        <span className="text-xs text-gray-600 dark:text-gray-400">
                          · {exp.company.city.name}
                        </span>
                      )}
                      <span
                        className={classNames(
                          "text-xs font-medium px-1.5 py-0.5 rounded",
                          exp.relevant
                            ? "bg-green-50 text-green-700 dark:bg-green-900/30 dark:text-green-400"
                            : "bg-gray-100 text-gray-500 dark:bg-gray-700 dark:text-gray-400",
                        )}
                      >
                        {exp.relevant ? "Relevant" : "Not relevant"}
                      </span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </SectionCard>

        {/* ── Education ────────────────────────────────────────────── */}
        <SectionCard title="Education">
          {!candidate?.education || candidate.education.length === 0 ? (
            <p className="text-sm text-gray-600 dark:text-gray-400">
              Candidate does not have any education
            </p>
          ) : (
            <div className="divide-y divide-gray-100 dark:divide-gray-700 -mx-6">
              {candidate.education.map((edu, idx) => (
                <div
                  key={edu.id ?? idx}
                  className="flex items-start gap-4 px-6 py-4 hover:bg-gray-50 dark:hover:bg-gray-800/40 transition-colors"
                >
                  <div className="h-9 w-9 rounded-lg bg-gray-100 dark:bg-gray-700 flex items-center justify-center flex-shrink-0">
                    <AcademicCapIcon className="h-4 w-4 text-gray-600 dark:text-gray-400" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold text-gray-900 dark:text-gray-100">
                      {edu.university?.name ?? "—"}
                    </p>
                    <p className="text-sm text-gray-500 dark:text-gray-400">
                      {edu.postGrad ? "Post Graduate" : "Under Graduate"}
                      {edu.branch?.name ? ` · ${edu.branch.name}` : ""}
                    </p>
                    <div className="flex flex-wrap items-center gap-x-3 gap-y-0.5 mt-1">
                      <span className="text-xs text-gray-600 dark:text-gray-400">
                        {edu.period}
                      </span>
                      {edu.cgpa ? (
                        <span className="text-xs text-gray-600 dark:text-gray-400">
                          · {edu.cgpa} CGPA
                        </span>
                      ): null}
                      <span
                        className={classNames(
                          "text-xs font-medium px-1.5 py-0.5 rounded",
                          edu.relevant
                            ? "bg-green-50 text-green-700 dark:bg-green-900/30 dark:text-green-400"
                            : "bg-gray-100 text-gray-500 dark:bg-gray-700 dark:text-gray-400",
                        )}
                      >
                        {edu.relevant ? "Relevant" : "Not relevant"}
                      </span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </SectionCard>

        {/* ── Candidate Role Answers ───────────────────────────────── */}
        {candidateRole[0]?.hasQuestionairre && (
          <SectionCard title="Role Specific Questionairre">
            {groupedCandidateRoleAnswers.length === 0 ? (
              <p className="text-sm text-gray-600 dark:text-gray-400">
                Candidate has not submitted role questionnaire responses yet.
              </p>
            ) : (
              <div className="space-y-3">
                {groupedCandidateRoleAnswers.map((item, index) => (
                  <div
                    key={`${item.questionText}-${index}`}
                    className="p-4 bg-gray-50 dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700"
                  >
                    <p className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-1">
                      Question {index + 1}
                    </p>
                    <p className="text-sm font-medium text-gray-900 dark:text-gray-100">
                      {parse(item.questionText || "-")}
                    </p>
                    <div className="mt-3 pt-3 border-t border-gray-200 dark:border-gray-700">
                      <p className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-1">
                        Candidate response
                      </p>
                      {item.answers.length === 0 ? (
                        <p className="text-sm text-gray-700 dark:text-gray-300 whitespace-pre-wrap break-words">
                          -
                        </p>
                      ) : (
                        <div className="flex flex-wrap gap-2">
                          {item.answers.map((answer, answerIndex) => (
                            <span
                              key={`${item.questionText}-${answer}-${answerIndex}`}
                              className="inline-flex items-center rounded-md bg-white dark:bg-gray-700 px-2 py-1 text-xs text-gray-700 dark:text-gray-200 border border-gray-200 dark:border-gray-600"
                            >
                              {answer}
                            </span>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </SectionCard>
        )}

        {/* ── Candidate Metrics ────────────────────────────────────── */}
        <SectionCard title="Candidate Metrics">
          {candidate?.combinedGrade != null ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <button
                onClick={() => openGradeModal("combined")}
                className="text-left p-4 bg-orange-50 dark:bg-orange-900/20 border border-orange-200 dark:border-orange-700 rounded-xl hover:shadow-md transition-shadow"
              >
                <p className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-1">
                  CV Grade (
                  {candidate?.combinedGradePercentile?.toFixed(2) ?? "N/A"}%ile)
                </p>
                <p className="text-xl font-bold text-gray-900 dark:text-gray-100">
                  {candidate?.combinedGrade?.toFixed(2) ?? "N/A"}
                </p>
              </button>
              <button
                onClick={() => openGradeModal("gap")}
                className="text-left p-4 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-700 rounded-xl hover:shadow-md transition-shadow"
              >
                <p className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-1">
                  Gap Years ({candidate?.gappercentile?.toFixed(2) ?? "N/A"}
                  %ile)
                </p>
                <p className="text-xl font-bold text-gray-900 dark:text-gray-100">
                  {candidate?.totalGapMonths ?? 0}
                </p>
              </button>
            </div>
          ) : (
            <p className="text-sm text-gray-600 dark:text-gray-400">Candidate is not graded yet</p>
          )}
        </SectionCard>

        {/* ── Assignment ───────────────────────────────────────────── */}
        {githublink.length > 0 && (
          <SectionCard title="Assignment">
            <div className="space-y-3">
              <a
                href={liveProjectLink}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-3 p-4 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-700 rounded-xl hover:shadow-md transition-shadow group"
              >
                <GlobeAltIcon
                  className="h-5 w-5 flex-shrink-0 text-entntblue group-hover:text-entntorange transition-colors"
                  aria-hidden="true"
                />
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-gray-900 dark:text-gray-100">
                    Live Project
                  </p>
                  <p className="text-xs text-gray-500 dark:text-gray-400 truncate mt-0.5">
                    {liveProjectLink}
                  </p>
                </div>
                <span className="text-entntblue group-hover:text-entntorange transition-colors">
                  →
                </span>
              </a>
              <a
                href={githublink}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-3 p-4 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl hover:shadow-md transition-shadow group"
              >
                <GitHubIcon
                  className="h-5 w-5 flex-shrink-0 text-gray-600 dark:text-gray-400 group-hover:text-entntblue transition-colors"
                  aria-hidden="true"
                />
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-gray-900 dark:text-gray-100">
                    GitHub Repository
                  </p>
                  <p className="text-xs text-gray-500 dark:text-gray-400 truncate mt-0.5">
                    {githublink}
                  </p>
                </div>
                <span className="text-entntblue group-hover:text-entntorange transition-colors">
                  →
                </span>
              </a>
            </div>
            <div className="mt-4 pt-4 border-t border-gray-100 dark:border-gray-700">
              <button
                onClick={() => setShowReviewbox(!showReviewbox)}
                className={classNames(
                  "w-full px-4 py-2 rounded-xl text-sm font-medium transition-all",
                  showReviewbox
                    ? "bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-400 border border-red-200 dark:border-red-800 hover:bg-red-100"
                    : "bg-entntblue hover:bg-entntblue/90 text-white",
                )}
              >
                {showReviewbox ? "Cancel Review" : "Write Review"}
              </button>
            </div>
            {showReviewbox && (
              <div className="mt-4 p-4 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl">
                <Review
                  onCommentSubmit={handleCommentSubmit}
                  previousReview={previousReview}
                  candidateId={candidateId}
                  roleId={roleId}
                />
              </div>
            )}
          </SectionCard>
        )}
      </section>

      <PDFViewerModal
        isOpen={pdfModal.isOpen}
        onClose={closePDFModal}
        pdfUrl={pdfModal.pdfUrl}
        fileName={pdfModal.fileName}
      />
      {candidate && gradeModalState.type && (
        <GradeMetricsModal
          isOpen={gradeModalState.isOpen}
          onClose={closeGradeModal}
          candidate={candidate}
          type={gradeModalState.type}
        />
      )}
    </>
  );
}
